import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LagunaCreekLogo from "../../assets/lagunacreek-logo.svg";
import LinkedIn from "../../assets/linkedIn.svg";
import Instagram from "../../assets/instagram.svg";
import Facebook from "../../assets/facebook.svg";
import Phone from "../../assets/phone.svg";
import Email from "../../assets/email.svg";
import { routeList } from "../../config/routeConfig";

const Footer = () => {
  const navigationItems = routeList.filter((a) => a.showMenuBar);
  const [footerData, setFooterData] = useState(null);

  useEffect(() => {
    fetch("https://www.lagunacreek.io/blog/wp-json/custom/v1/footer-settings")
      .then((res) => res.json())
      .then((data) => setFooterData(data))
      .catch((error) => {
        console.error("Failed to load footer data:", error);
        setFooterData(null);
      });
  }, []);

  const socialIcons = [
    {
      defaultIcon: <div className="font-bold text-white">X</div>,
      fallbackLink: "https://x.com/laguna_creek",
    },
    { icon: Facebook, fallbackLink: "https://www.facebook.com/profile.php?id=61564408615596" },
    { icon: Instagram, fallbackLink: "https://www.instagram.com/lagunacreek2024/" },
    { icon: LinkedIn, fallbackLink: "https://www.linkedin.com/company/lcpms" },
  ];

  return (
    <footer className="pt-10 md:pt-16 pb-5 border-t border-[#E7E7E7]">
      <div className="w-full max-w-[1570px] px-4 mx-auto">
        <div className="flex flex-wrap justify-between -mx-4 gap-y-[32px]">
          {/* Logo + About */}
          <div className="w-full xl:w-auto px-4">
            <div className="flex flex-col gap-[35px] xl:max-w-[398px]">
              <Link to="/">
                <img
                  src={footerData?.footer_logo || LagunaCreekLogo}
                  alt="Laguna Creek Logo"
                  className="w-auto"
                  height="159"
                  width="59"
                />
              </Link>
              <p className="text-lg font-light leading-7 text-[#192020]">
                {footerData?.footer_about_us_text ||
                  "Boosting the Indigenous economy by keeping more tourism, retail and hospitality dollars on Indigenous land."}
              </p>
              <div className="flex items-center gap-3">
                {(footerData?.social_media || []).length
                  ? footerData.social_media.map((item, index) => (
                      <Link
                        key={index}
                        to={item.social_icon_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex justify-center items-center w-[34px] h-[34px] bg-[#D4D4D4] border border-[#D4D4D4] rounded-full transition-all hover:bg-[#5fa3b2] hover:border-[#5fa3b2]"
                      >
                        <img src={item.social_icon} alt={`social-icon-${index}`} />
                      </Link>
                    ))
                  : socialIcons.map((icon, index) => (
                      <Link
                        key={index}
                        to={icon.fallbackLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex justify-center items-center w-[34px] h-[34px] bg-[#D4D4D4] border border-[#D4D4D4] rounded-full transition-all hover:bg-[#5fa3b2] hover:border-[#5fa3b2]"
                      >
                        {icon.icon ? <img src={icon.icon} alt="social" /> : icon.defaultIcon}
                      </Link>
                    ))}
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div className="w-auto md:w-1/3 lg:w-auto px-4">
            <h2 className="text-[22px] font-normal leading-8 text-[#192020] mb-5">
              {footerData?.company_title || "Company"}
            </h2>
            <ul className="flex flex-col items-center gap-[22px]">
              {(footerData?.company || navigationItems).map((item, index) => (
                <li className="w-full" key={index}>
                  {item.company_menu_title ? (
                    <Link
                      to={item.company_menu_link}
                      className="text-lg font-light leading-7 text-[#333333] hover:text-[#5FA3B2]"
                      dangerouslySetInnerHTML={{ __html: item.company_menu_title }}
                    />
                  ) : (
                    <Link
                      to={item.path}
                      className="text-lg font-light leading-7 text-[#333333] hover:text-[#5FA3B2]"
                    >
                      {item.menuLabel}
                    </Link>
                  )}
                </li>
              ))}
              {!footerData?.company && (
                <li className="w-full">
                  <a
                    href="https://lagunacreek.io/blog/"
                    className="text-lg font-light leading-7 text-[#333333] hover:text-[#5FA3B2]"
                  >
                    Blog
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Website Menu */}
          <div className="w-auto md:w-1/3 lg:w-auto px-4">
            <h2 className="text-[22px] font-normal leading-8 text-[#192020] mb-5">
              {footerData?.website_title || "Website"}
            </h2>
            <ul className="flex flex-col items-center gap-[22px]">
              {(footerData?.website_menu || [
                { website_menu_text: "Privacy Policy", website_menu_link: "/privacy-policy" },
                { website_menu_text: "Terms of Use", website_menu_link: "/terms-of-use" },
              ]).map((item, index) => (
                <li className="w-full" key={index}>
                  <Link
                    to={item.website_menu_link}
                    className="text-lg font-light leading-7 text-[#333333] hover:text-[#5FA3B2]"
                  >
                    {item.website_menu_text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect With Us */}
          <div className="w-auto md:w-1/3 lg:w-auto px-4">
            <h2 className="text-[22px] font-normal leading-8 text-[#192020] mb-5">
              {footerData?.connect_with_us_title || "Connect With Us"}
            </h2>
            <ul className="flex flex-col items-center gap-[7px]">
              {(footerData?.connect_with_us || [
                {
                  connect_with_us_item_icon: Phone,
                  connect_with_us_item_text: "(623) 300-2434",
                  connect_with_us_item_link: "tel:+16233002434",
                },
                {
                  connect_with_us_item_icon: Email,
                  connect_with_us_item_text: "info@lagunacreek.com",
                  connect_with_us_item_link: "mailto:info@lagunacreek.com",
                },
              ]).map((item, index) => (
                <li className="w-full flex items-center gap-[10px]" key={index}>
                  <img src={item.connect_with_us_item_icon} alt="" className="w-auto" />
                  <Link
                    to={item.connect_with_us_item_link}
                    className="text-lg font-light leading-7 text-[#333333] hover:text-[#5FA3B2]"
                  >
                    {item.connect_with_us_item_text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="w-full max-w-[1570px] px-4 mx-auto">
        <div className="flex flex-wrap -mx-4 mt-9">
          <div className="w-full max-w-full px-4 mb-5">
            <div className="border-t border-[#E7E7E7]"></div>
          </div>
          <div className="w-full max-w-full px-4">
            <p className="text-sm sm:text-base font-light leading-7 text-[#A7A7A7]">
              {footerData?.copyright_text ||
                `Copyright ${new Date().getFullYear()} Laguna Creek. All rights reserved.`}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
